import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { message, Input, Form, Tag, Tooltip, Checkbox, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { JoinAndManageContext } from './../../activity-receipt-context';
import { STARANDMANAGETYPEENUM, MYFOLLOWKEYNUM } from './../../activity-receipt-config';
import apis from '../../apis';
import './index.less';

/****************
 * 分组管理及收藏【二者主要区别就是:收藏里面不能删除且有弹窗确认取消按钮】【枚举类型:STARANDMANAGETYPEENUM】
 * *****************/
export default function StarAndManageWrapper(props) {
  const { modalType, isModalVisible, setIsModalVisible, onSuccess, activityId } = props;
  const [groupList, memoizedGetGroupList] = useContext(JoinAndManageContext);

  /***************** 下方tag逻辑 *****************/
  const [modalForm] = Form.useForm();
  const inputRef = useRef(null);
  const editInputRef = useRef(null);
  const [tags, setTags] = useState([]);
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [editInputIndex, setEditInputIndex] = useState(-1);
  const [editInputValue, setEditInputValue] = useState('');
  const myFollowHasThisActivity = useRef(null);
  const modalContentHasModify = useRef(false);
  const modalContentCountOld = useRef(0);
  const modalContentCountNew = useRef(0);

  // 回显【'关注类型'时才有】
  const memoizedGetPreActivityGroup = useCallback(async () => {
    if (modalType === STARANDMANAGETYPEENUM.STARTYPE && activityId && modalForm) {
      // 我的关注和我的自建分组
      const {
        ret: watchRet,
        data: { data: watchData },
      } = await apis.fetchaActivityWatchIsWatch({
        activityId,
      });
      const {
        ret: groupRet,
        data: { data: groupData },
      } = await apis.fetchaActivityCollectionGetgroup({
        activityId,
      });

      const concatArr = watchData
        ? [MYFOLLOWKEYNUM, ...groupData.map((item) => item.groupId)]
        : groupData.map((item) => item.groupId);

      modalForm?.setFieldsValue({
        checkboxGroup: concatArr,
      });

      // 记录一下初始化时，我的关注里有没有这个活动【我的关注有没有被选中】
      myFollowHasThisActivity.current = watchData;

      // 记录下进入时和提交时二者的数量有无变化，来决定要不要执行成功回到，来更改列表状态
      modalContentCountOld.current = concatArr?.length || 0;
      modalContentCountNew.current = concatArr?.length || 0;
    }
  }, [modalType, activityId, modalForm]);

  useEffect(() => {
    if (isModalVisible) {
      setTags(groupList || []);
      memoizedGetPreActivityGroup();
    }
  }, [groupList, isModalVisible, memoizedGetPreActivityGroup]);

  useEffect(() => {
    if (editInputIndex !== -1) {
      editInputRef.current?.focus();
    }
    if (inputVisible) {
      inputRef.current?.focus();
    }
  }, [editInputIndex, inputVisible]);

  function showInput() {
    setInputVisible(true);
  }
  function handleInputChange(e) {
    setInputValue(e.target.value);
  }
  function handleShowEditInput(e, tag, index) {
    //我的关注【groupId:-1】是不能编辑的
    if (tag.groupId === MYFOLLOWKEYNUM) {
      e.persist();
      return false;
    }
    setEditInputIndex(index);
    setEditInputValue(tag.name);
    e.persist();
  }
  function handleEditInputChange(e) {
    setEditInputValue(e.target.value);
  }

  /***************** tag增、删、改 *****************/
  async function handleInputConfirm() {
    if (inputValue === '' || inputValue == null || inputValue == undefined) {
      setInputVisible(false);
      setInputValue('');
      return false;
    }
    if (inputValue && inputValue.length > 8) {
      setInputVisible(false);
      setInputValue('');
      message.error(`字数不能超过8个`);
      return false;
    }
    const { ret, data, msg } = await apis.fetchaActivityCollectionAdd({
      name: inputValue,
    });

    if (ret === '0' && data) {
      const newtags = [...tags, data];
      setTags(newtags);
      message.success(`新建成功！`);
      memoizedGetGroupList();
    } else {
      message.error(`${msg || '新建失败，请您稍后重试！'}`);
    }

    setInputVisible(false);
    setInputValue('');
  }

  async function handleClose(removedTag) {
    if (!removedTag) return false;
    const { ret, msg } = await apis.fetchaActivityCollectionDel({
      groupId: removedTag.groupId,
    });
    if (ret === '0') {
      const newtags = tags.filter((tag) => tag.groupId !== removedTag.groupId);
      setTags(newtags);
      message.success(`删除成功！`);
      memoizedGetGroupList();
    } else {
      message.error(`${msg || '删除失败，请您稍后重试！'}`);
    }
  }

  async function handleEditInputConfirm(modtag) {
    if (
      editInputValue === '' ||
      editInputValue == null ||
      editInputValue == undefined ||
      modtag.name === editInputValue //没有修改也不走接口
    ) {
      setEditInputIndex(-1);
      setEditInputValue('');
      return false;
    }
    if (editInputValue && editInputValue.length > 8) {
      setEditInputIndex(-1);
      setEditInputValue('');
      message.error(`字数不能超过8个`);
      return false;
    }

    const { ret, data } = await apis.fetchaActivityCollectionMod({
      groupId: modtag.groupId,
      name: editInputValue,
    });

    if (ret === '0' && data) {
      const modIndex = tags.findIndex((item) => item.groupId === modtag.groupId);
      let newTags = [...tags];
      newTags.splice(modIndex, 1, data);
      setTags(newTags);
      message.success(`修改成功！`);
      memoizedGetGroupList();
    } else {
      message.error(`修改失败！`);
    }

    setEditInputIndex(-1);
    setEditInputValue('');
  }

  /***************** modal行为处理 *****************/
  function okSubmit() {
    modalForm
      .validateFields()
      .then((values) => {
        submitForm(values);
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  }

  async function submitForm({ checkboxGroup: checkboxGroupArr }) {
    // 去处理关于我的关注的逻辑【要实施两个接口,取消我的关注和增加我的关注逻辑】
    const initMyFollowStatus = myFollowHasThisActivity.current;
    const nowMyFollowStatus = checkboxGroupArr.includes(MYFOLLOWKEYNUM);
    // 二者不相等，代表有变化，需要掉对应接口
    if (initMyFollowStatus !== nowMyFollowStatus) {
      if (initMyFollowStatus && !nowMyFollowStatus) {
        // 取消我的关注接口
        const { ret, data } = await apis.fetchaActivityWatchCancel({ id: activityId });
        if (ret === '0') {
          modalContentHasModify.current = true;
        }
      } else if (!initMyFollowStatus && nowMyFollowStatus) {
        // 新增我的关注接口
        const { ret, data } = await apis.fetchaActivityWatchCreate({ id: activityId });
        if (ret === '0') {
          modalContentHasModify.current = true;
        }
      }
    }

    // 统一处理分配到组
    let checkboxGroupArrNoFollow = checkboxGroupArr;
    if (nowMyFollowStatus) {
      checkboxGroupArrNoFollow = checkboxGroupArr.filter((item) => item !== MYFOLLOWKEYNUM);
    }
    const {
      ret,
      data: { data: setGroupData },
    } = await apis.fetchaActivityCollectionSetgroup({
      groupIds: checkboxGroupArrNoFollow.length > 0 ? checkboxGroupArrNoFollow.join() : 1, // 全量传递自己建立的分组id，如果自己建立的分组都未选中就传1
      activityId,
    });
    if (ret === '0') {
      modalContentHasModify.current = true;
    }

    // 所有行为统一回调

    cancelModal(checkboxGroupArr);
  }

  function cancelModal(checkboxGroupArr = null) {
    setTags([]);
    setIsModalVisible(false);
    setInputValue('');
    setEditInputIndex(-1);
    setEditInputValue('');
    myFollowHasThisActivity.current = null;
    modalForm.resetFields();
    //只要框内有增删改查或其他接口行为都刷新
    if (modalContentHasModify.current) {
      memoizedGetGroupList();
      if (checkboxGroupArr) {
        if (
          (modalContentCountOld.current === 0 && checkboxGroupArr.length > 0) ||
          (modalContentCountOld.current > 0 && checkboxGroupArr.length === 0)
        ) {
          onSuccess && onSuccess();
        }
      }
    }
    modalContentCountOld.current = 0;
    modalContentHasModify.current = false;
  }

  return (
    <div className="star-and-manage-modal-fa">
      {modalType === STARANDMANAGETYPEENUM.MANAGETYPE ? (
        <div className="modal-top-tips">删除：仅删除分组，不删除活动</div>
      ) : null}

      <div className="modal-group-list-fa">
        <div
          className="modal-group-list-operation-fa"
          style={{ maxHeight: modalType === STARANDMANAGETYPEENUM.STARTYPE ? '140px' : '172px' }}
        >
          <div className="modal-group-list-common-fa">
            <Form name="form" form={modalForm} className="modal-group-list-common-form-fa">
              <Form.Item name="checkboxGroup" style={{ marginBottom: 0 }}>
                <Checkbox.Group className="group-checkbox-item-fa">
                  {tags &&
                    tags.map((tag, index) => {
                      let shouldShowTagItemDom = null;
                      if (editInputIndex === index) {
                        shouldShowTagItemDom = (
                          <Input
                            ref={editInputRef}
                            size="small"
                            className="tag-input edit-tag-item"
                            value={editInputValue}
                            onChange={handleEditInputChange}
                            onBlur={debounce(() => handleEditInputConfirm(tag), 300)}
                            onPressEnter={debounce(() => handleEditInputConfirm(tag), 300)}
                          />
                        );
                      } else {
                        const isLongTag = tag?.name?.length > 8;
                        const tagElem = (
                          <Tag
                            className="edit-tag edit-tag-item"
                            closable={!(tag.groupId === -1 || modalType === STARANDMANAGETYPEENUM.STARTYPE)} //我的关注【groupId:-1】或者关注弹窗类型不能删
                            onClose={debounce(() => handleClose(tag), 300)}
                          >
                            <span
                              onDoubleClick={debounce((e) => handleShowEditInput(e, tag, index), 300)}
                              className="edit-tag-txt"
                            >
                              {isLongTag ? `${tag?.name?.slice(0, 8)}...` : tag?.name}
                            </span>
                          </Tag>
                        );
                        shouldShowTagItemDom = isLongTag ? (
                          <Tooltip title={tag?.name}>{tagElem}</Tooltip>
                        ) : (
                          <>{tagElem}</>
                        );
                      }

                      return (
                        <span key={tag?.groupId} className="group-list-tag-item-fa">
                          {modalType === STARANDMANAGETYPEENUM.STARTYPE && (
                            <Checkbox value={tag.groupId} className="group-checkbox-item" />
                          )}
                          {shouldShowTagItemDom}
                        </span>
                      );
                    })}
                </Checkbox.Group>
              </Form.Item>
            </Form>
          </div>
        </div>

        <div
          className={classnames(
            'modal-group-list-add-fa',
            modalType === STARANDMANAGETYPEENUM.STARTYPE ? 'add-fa-ml' : ''
          )}
        >
          {inputVisible ? (
            <Input
              ref={inputRef}
              type="text"
              size="small"
              className="tag-input"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={debounce(handleInputConfirm, 300)}
              onPressEnter={debounce(handleInputConfirm, 300)}
            />
          ) : tags && Array.isArray(tags) && tags.length >= 10 ? (
            <Tooltip title={'已达到10个上限，不可再添加'}>
              <Button
                type="dashed"
                disabled
                size={'small'}
                className="site-span-plus-disabled"
                style={{
                  height: '22px',
                  lineHeight: '18px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <PlusOutlined className="site-span-plus-icon" />
                <span className="site-span-plus-txt">新建分组</span>
              </Button>
            </Tooltip>
          ) : (
            <Button
              type="dashed"
              size={'small'}
              onClick={debounce(() => showInput(), 200)}
              className="site-span-plus"
              style={{ height: '22px' }}
            >
              <PlusOutlined className="site-span-plus-icon" />
              <span className="site-span-plus-txt">新建分组</span>
            </Button>
          )}
        </div>
      </div>

      {modalType === STARANDMANAGETYPEENUM.STARTYPE ? (
        <div className="modal-btns-fa">
          <span className="modal-btn-item modal-cancle-btn" onClick={debounce(() => cancelModal(), 300)}>
            取消
          </span>
          <span className="modal-btn-item modal-sure-btn" onClick={debounce(() => okSubmit(), 300)}>
            确定
          </span>
        </div>
      ) : null}
    </div>
  );
}
